import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabaseClient';
import WebsiteSectionEditor from '../../components/website/WebsiteSectionEditor';

// API base selection:
// - If env is provided, use it.
// - Otherwise, on production frontend (www.explanatum.co.uk), use relative paths to leverage Vercel rewrites.
// - In local dev, setupProxy handles relative '/api'.
const API_BASE = (() => {
  const raw = (process.env.REACT_APP_API_URL || (process.env as any).NEXT_PUBLIC_API_BASE || '').replace(/\/$/, '');
  if (raw) return raw;
  if (typeof window !== 'undefined') {
    const h = window.location.hostname || '';
    if (h.endsWith('explanatum.co.uk')) return '';
  }
  return '';
})();

// Types for safer data handling
interface V3Section {
  id: string;
  kind?: string;
  heading?: string;
  body?: string;
  html?: string;
  [key: string]: any;
}

// API response types
interface GenerateV3Response {
  websiteData?: GeneratedWebsite;
  v3?: { metadata?: V3Metadata; sections?: V3Section[] };
}

interface AssembleResponse { html?: string }
interface SaveWebsiteResponse { website: { id: string } }
interface DeployResponse { url?: string; deploymentId?: string }
interface ConnectDomainResponse {
  status?: 'pending' | 'verified' | 'error';
  dnsRecords?: Array<{ type: string; name: string; value: string; ttl?: number }>
}
interface VerifyDomainResponse { status?: 'pending' | 'verified' | 'error' }

interface V3Metadata {
  [key: string]: any;
}

interface GeneratedWebsite {
  title: string;
  description?: string;
  tagline?: string;
  businessLogoUrl?: string;
  businessName?: string;
  businessAddress?: string;
  aboutUs?: string;
  contactEmail?: string;
  contactPhone?: string;
  html: string;
  pages?: any[];
  [key: string]: any;
}

const AIWebsiteGenerator: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [websiteDescription, setWebsiteDescription] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeploying, setIsDeploying] = useState(false);
  const [generatedWebsite, setGeneratedWebsite] = useState<GeneratedWebsite | null>(null);
  const [websiteId, setWebsiteId] = useState<string | null>(null);
  const [deploymentStatus, setDeploymentStatus] = useState<string | null>(null);
  const [deploymentUrl, setDeploymentUrl] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  // Simple page phase state machine
  const [phase, setPhase] = useState<'idle' | 'generating' | 'editing' | 'saving' | 'saved' | 'deploying' | 'deployed' | 'error'>('idle');
  const [attemptedGenerate, setAttemptedGenerate] = useState(false);
  // V3 structured inputs
  const [businessName, setBusinessName] = useState('');
  const [industry, setIndustry] = useState('');
  const [location, setLocation] = useState('');
  const [tone, setTone] = useState('modern');
  const [colorMood, setColorMood] = useState('neutral');
  const [templateKey, setTemplateKey] = useState<'modern' | 'minimal' | 'elegant' | 'bold'>('modern');
  const [vertical, setVertical] = useState<'it_support'|'dental'|'consultancy'|'saas'|'restaurant'|''>('');
  const [palette, setPalette] = useState<'neutral' | 'cool' | 'warm' | 'vibrant'>('neutral');
  const [brandAccent, setBrandAccent] = useState<string>('');
  const [brandFont, setBrandFont] = useState<'system' | 'serif' | 'rounded'>('system');
  const [logoUrl, setLogoUrl] = useState('');
  const [brandLogoPlacement, setBrandLogoPlacement] = useState<'nav' | 'hero'>('nav');
  const [heroImageUrl, setHeroImageUrl] = useState<string>('');
  const [showLogos, setShowLogos] = useState<boolean>(true);
  const [logoStoragePath, setLogoStoragePath] = useState<string>('');
  const [targetAudience, setTargetAudience] = useState('');
  // Sector-specific extras (optional)
  const [dentalBookingUrl, setDentalBookingUrl] = useState('');
  const [dentalInsuranceText, setDentalInsuranceText] = useState('');
  const [dentalHours, setDentalHours] = useState('');
  const [itSlaP1, setItSlaP1] = useState('');
  const [consultancyDiscoveryUrl, setConsultancyDiscoveryUrl] = useState('');
  const [saasIntegrations, setSaasIntegrations] = useState('');
  const [saasTrialUrl, setSaasTrialUrl] = useState('');
  const [saasSSO, setSaasSSO] = useState(false);
  const [saasSOC2, setSaasSOC2] = useState(false);
  const [restaurantReservationsUrl, setRestaurantReservationsUrl] = useState('');
  const [restaurantDietaryTags, setRestaurantDietaryTags] = useState('');
  const [restaurantHours, setRestaurantHours] = useState('');
  // Logo upload state
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [logoPreview, setLogoPreview] = useState<string>('');
  // V3 data for per-section controls
  const [v3Metadata, setV3Metadata] = useState<V3Metadata | null>(null);
  const [v3Sections, setV3Sections] = useState<V3Section[]>([]);
  const assembleTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const assembleAbortRef = useRef<AbortController | null>(null);
  
  // Preview state
  const [showPreview, setShowPreview] = useState(false);
  // Domain modal state
  const [domainModalOpen, setDomainModalOpen] = useState(false);
  const [domainInput, setDomainInput] = useState('');
  const [domainSubmitting, setDomainSubmitting] = useState(false);
  const [domainError, setDomainError] = useState('');
  const [domainDnsRecords, setDomainDnsRecords] = useState<Array<{type:string; name:string; value:string; ttl?:number}> | null>(null);
  const [domainVerifyStatus, setDomainVerifyStatus] = useState<'pending' | 'verified' | 'error' | null>(null);
  const [domainVerifying, setDomainVerifying] = useState(false);

  // Deployment status/logs
  const [deploymentId, setDeploymentId] = useState<string | null>(null);
  const [deploymentLogs, setDeploymentLogs] = useState<string[]>([]);
  const deploymentPollRef = useRef<number | null>(null);
  const [showDeploymentLogs, setShowDeploymentLogs] = useState(false);
  // Template preview
  const [templatePreviewOpen, setTemplatePreviewOpen] = useState(false);
  const [templatePreviewLoading, setTemplatePreviewLoading] = useState(false);
  const [templatePreviewHtml, setTemplatePreviewHtml] = useState<string>('');
  const [templatePreviewError, setTemplatePreviewError] = useState<string>('');
  const [previewTemplateKey, setPreviewTemplateKey] = useState<'modern' | 'minimal' | 'elegant' | 'bold'>('modern');
  const [templateMiniPreviewHtml, setTemplateMiniPreviewHtml] = useState<string>('');
  // Variant generation
  const [isGeneratingVariants, setIsGeneratingVariants] = useState(false);
  const [variantCount, setVariantCount] = useState<number>(6);
  const [variantsOpen, setVariantsOpen] = useState(false);
  const [variants, setVariants] = useState<Array<{ id: string; websiteData: GeneratedWebsite | null; metadata: V3Metadata | null; sections: V3Section[] }>>([]);
  const [variantPrompt, setVariantPrompt] = useState<string>('');
  const [favoriteVariantIds, setFavoriteVariantIds] = useState<Set<string>>(new Set());
  const [showFavoritesOnly, setShowFavoritesOnly] = useState<boolean>(false);
  // UX polish
  const [previewTall, setPreviewTall] = useState<boolean>(false);
  // Modal refs for a11y focus trap
  const templateModalRef = useRef<HTMLDivElement | null>(null);
  const templateModalCloseRef = useRef<HTMLButtonElement | null>(null);
  const websiteModalRef = useRef<HTMLDivElement | null>(null);
  const websiteModalCloseRef = useRef<HTMLButtonElement | null>(null);
  const domainModalRef = useRef<HTMLDivElement | null>(null);
  const domainModalCloseRef = useRef<HTMLButtonElement | null>(null);

  const { user } = useAuth();

  // Basic SEO per project convention
  useEffect(() => {
    document.title = 'AI Website Generator | Explanatum';
  }, []);

  // Prefill based on vertical/template query params
  useEffect(() => {
    const vertical = (searchParams.get('vertical') || '').toLowerCase();
    const tpl = (searchParams.get('template') || '').toLowerCase();
    if (tpl === 'modern' || tpl === 'minimal' || tpl === 'elegant' || tpl === 'bold') {
      setTemplateKey(tpl as any);
    }
    if (vertical === 'it_support' || vertical === 'dental' || vertical === 'consultancy' || vertical === 'saas' || vertical === 'restaurant') {
      setVertical(vertical as any);
    }
    // Only prefill if fields are empty to avoid clobbering user edits
    const setIfEmpty = (setter: (v: string)=>void, current: string, v: string) => {
      if (!current || current.trim().length === 0) setter(v);
    };
    switch (vertical) {
      case 'it_support':
        setIfEmpty(setIndustry, industry, 'IT Support');
        setIfEmpty(setWebsiteDescription, websiteDescription, 'Managed IT services, Microsoft 365 administration, proactive monitoring, and security & compliance for SMEs.');
        break;
      case 'dental':
        setIfEmpty(setIndustry, industry, 'Dental Clinic');
        setIfEmpty(setWebsiteDescription, websiteDescription, 'General & cosmetic dentistry, insurance accepted, online booking, friendly team and modern equipment.');
        break;
      case 'consultancy':
        setIfEmpty(setIndustry, industry, 'Consultancy');
        setIfEmpty(setWebsiteDescription, websiteDescription, 'Strategy and operations consultancy delivering measurable results, case studies and expert guidance.');
        break;
      case 'saas':
        setIfEmpty(setIndustry, industry, 'SaaS');
        setIfEmpty(setWebsiteDescription, websiteDescription, 'Modern SaaS product with powerful features, integrations, transparent pricing and enterprise-grade security.');
        break;
      case 'restaurant':
        setIfEmpty(setIndustry, industry, 'Restaurant');
        setIfEmpty(setWebsiteDescription, websiteDescription, 'Seasonal menu highlights, cozy ambience, reservations available, central location and excellent reviews.');
        break;
      default:
        break;
    }
  // Intentionally exclude setters to prevent infinite loops; rely on current values
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Keep palette synchronized with Color Mood select to avoid duplicate UI
  useEffect(() => {
    setPalette((colorMood as any) || 'neutral');
  }, [colorMood]);

  // Cleanup deployment polling on unmount
  useEffect(() => {
    return () => {
      if (deploymentPollRef.current) {
        window.clearInterval(deploymentPollRef.current);
        deploymentPollRef.current = null;
      }
      if (assembleTimeoutRef.current) {
        clearTimeout(assembleTimeoutRef.current);
      }
      if (assembleAbortRef.current) {
        assembleAbortRef.current.abort();
        assembleAbortRef.current = null;
      }
    };
  }, []);

  // Basic monitoring helper (placeholder for real monitoring service)
  const monitorError = (event: string, detail: unknown) => {
    try {
      // hook up to real monitoring here
      // eslint-disable-next-line no-console
      console.error(`[monitor] ${event}`, detail);
    } catch {}
  };

  // Computed CTA states derived from phase + data
  const canGenerate = !isGenerating && !!businessName.trim() && phase !== 'deploying' && phase !== 'saving';
  const canSave = !!generatedWebsite && !!generatedWebsite.html && generatedWebsite.html.trim().length > 0 && !isSaving && !websiteId && (phase === 'editing' || phase === 'saved');
  const canDeploy = !!websiteId && !isDeploying && deploymentStatus !== 'deployed';

  // Reset all inputs and state to start over
  const resetForm = () => {
    setBusinessName('');
    setIndustry('');
    setLocation('');
    setTone('modern');
    setColorMood('neutral');
    setTemplateKey('modern');
    setPalette('neutral');
    setLogoUrl('');
    setLogoStoragePath('');
    setTargetAudience('');
    setGeneratedWebsite(null);
    setWebsiteId(null);
    setDeploymentStatus(null);
    setDeploymentUrl(null);
    setError('');
    setStatusMessage('');
    setAttemptedGenerate(false);
    setV3Metadata(null);
    setV3Sections([]);
    setPhase('idle');
  };

  // Regenerate using current inputs and selected template
  const handleRegenerateUsingCurrentTemplate = async () => {
    setGeneratedWebsite(null);
    await handleGenerateWebsite();
  };

  // Update inline mini template preview when inputs that influence structure change
  useEffect(() => {
    try {
      const { metadata, sections } = buildPreviewContent();
      const html = renderTemplatePreviewHTML(templateKey, palette, metadata, sections);
      setTemplateMiniPreviewHtml(html);
    } catch {
      // ignore
    }
  }, [templateKey, palette, brandAccent, brandFont, showLogos, heroImageUrl, businessName, industry, websiteDescription, v3Metadata, v3Sections]);

  // Global ESC handler to close modals
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (templatePreviewOpen) setTemplatePreviewOpen(false);
        if (showPreview) setShowPreview(false);
        if (domainModalOpen) setDomainModalOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [templatePreviewOpen, showPreview, domainModalOpen]);

  // Focus trap for modals (basic)
  useEffect(() => {
    if (templatePreviewOpen && templateModalRef.current) {
      // initial focus
      templateModalCloseRef.current?.focus();
      const el = templateModalRef.current;
      const handler = (e: KeyboardEvent) => {
        if (e.key !== 'Tab') return;
        const focusables = el.querySelectorAll<HTMLElement>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      };
      el.addEventListener('keydown', handler as any);
      return () => el.removeEventListener('keydown', handler as any);
    }
  }, [templatePreviewOpen]);

  useEffect(() => {
    if (showPreview && websiteModalRef.current) {
      websiteModalCloseRef.current?.focus();
      const el = websiteModalRef.current;
      const handler = (e: KeyboardEvent) => {
        if (e.key !== 'Tab') return;
        const focusables = el.querySelectorAll<HTMLElement>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      };
      el.addEventListener('keydown', handler as any);
      return () => el.removeEventListener('keydown', handler as any);
    }
  }, [showPreview]);

  useEffect(() => {
    if (domainModalOpen && domainModalRef.current) {
      domainModalCloseRef.current?.focus();
      const el = domainModalRef.current;
      const handler = (e: KeyboardEvent) => {
        if (e.key !== 'Tab') return;
        const focusables = el.querySelectorAll<HTMLElement>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      };
      el.addEventListener('keydown', handler as any);
      return () => el.removeEventListener('keydown', handler as any);
    }
  }, [domainModalOpen]);

  // Robust JSON request helper: timeout + retry with backoff; handles non-JSON proxy errors gracefully
  const requestJson = async (url: string, options: RequestInit, defaultError: string, retries = 2, timeoutMs = 20000) => {
    const attempt = async (): Promise<any> => {
      const controller = new AbortController();
      const to = setTimeout(() => controller.abort(), timeoutMs);
      try {
        const resp = await fetch(url, { ...options, signal: controller.signal });
        clearTimeout(to);
        const ct = resp.headers.get('content-type') || '';

        // If not JSON, read text and surface a readable error
        if (!ct.toLowerCase().includes('application/json')) {
          const textBody = await resp.text();
          const snippet = (textBody || '').toString().slice(0, 300);
          if (!resp.ok) {
            throw new Error(`HTTP ${resp.status}: ${snippet || defaultError}`);
          }
          throw new Error(`Unexpected non-JSON response: ${snippet || defaultError}`);
        }

        // JSON path
        let data: any = null;
        try {
          data = await resp.json();
        } catch (e) {
          throw new Error(`Failed to parse JSON: ${defaultError}`);
        }

        if (!resp.ok) {
          const msg = (data && (data.error || data.message)) || defaultError;
          throw new Error(`HTTP ${resp.status}: ${msg}`);
        }

        return data;
      } catch (err: any) {
        clearTimeout(to);
        // Retry on network/timeout errors
        const msg = String(err?.message || '').toLowerCase();
        const isAbort = err?.name === 'AbortError' || msg.includes('timeout') || msg.includes('failed to fetch') || msg.includes('network') || msg.includes('wsarecv') || msg.includes('unavailable');
        if (retries > 0 && isAbort) {
          const backoff = (3 - retries) * 1000 + Math.floor(Math.random() * 500);
          await new Promise(r => setTimeout(r, backoff));
          return attempt.bind(null)();
        }
        throw err;
      }
    };
    return attempt();
  };

  // Build a minimal payload to preview templates deterministically
  const buildPreviewContent = (): { metadata: V3Metadata; sections: V3Section[] } => {
    if (v3Metadata && v3Sections && v3Sections.length > 0) {
      return { metadata: v3Metadata, sections: v3Sections };
    }
    const bn = businessName?.trim() || 'Your Business';
    const desc = websiteDescription?.trim() || `We offer ${industry || 'great services'}.`;
    const meta: V3Metadata = {
      title: bn,
      description: desc,
      bookingUrl: dentalBookingUrl || undefined,
      insuranceText: dentalInsuranceText || undefined,
      hours: (vertical === 'dental' ? dentalHours : restaurantHours) || undefined,
      slaP1: itSlaP1 || undefined,
      discoveryUrl: consultancyDiscoveryUrl || undefined,
      integrations: saasIntegrations || undefined,
      trialUrl: saasTrialUrl || undefined,
      sso: saasSSO || undefined,
      soc2: saasSOC2 || undefined,
      reservationsUrl: restaurantReservationsUrl || undefined,
      dietaryTags: restaurantDietaryTags || undefined,
    };

    // Vertical-shaped defaults to keep client preview in parity with server
    const shaped: Record<string, V3Section[]> = {
      it_support: [
        { id: 'hero', kind: 'hero', heading: bn, body: desc },
        { id: 'services', kind: 'services', heading: 'Managed IT & Security', body: 'Proactive monitoring, Microsoft 365, backups & DR.' },
        { id: 'pricing', kind: 'pricing', heading: 'Plans', body: 'Starter, Growth, Enterprise' },
        { id: 'faq', kind: 'faq', heading: 'FAQ', body: 'Response times, remote support, audits' },
        { id: 'contact', kind: 'contact', heading: 'Contact', body: `Get in touch at ${generatedWebsite?.contactEmail || 'info@example.com'}` },
      ],
      dental: [
        { id: 'hero', kind: 'hero', heading: bn, body: 'General & cosmetic dentistry. Friendly team. Book online.' },
        { id: 'services', kind: 'services', heading: 'Treatments', body: 'Check-ups, hygiene, whitening, Invisalign.' },
        { id: 'testimonials', kind: 'testimonials', heading: 'Happy Patients', body: 'Real reviews from local patients.' },
        { id: 'faq', kind: 'faq', heading: 'FAQ', body: 'Insurance, booking, first visit' },
        { id: 'contact', kind: 'contact', heading: 'Visit Us', body: 'Hours, address, phone, booking link' },
      ],
      consultancy: [
        { id: 'hero', kind: 'hero', heading: bn, body: 'Strategy and operations that deliver measurable results.' },
        { id: 'services', kind: 'services', heading: 'Practice Areas', body: 'Operations, GTM, transformation.' },
        { id: 'case-studies', kind: 'case-studies', heading: 'Case Studies', body: 'Outcomes and impact we drove.' },
        { id: 'faq', kind: 'faq', heading: 'FAQ', body: 'Engagement model, timeline, deliverables' },
        { id: 'contact', kind: 'contact', heading: 'Contact', body: 'Let’s discuss your goals.' },
      ],
      saas: [
        { id: 'hero', kind: 'hero', heading: bn, body: 'Modern SaaS with powerful features and integrations.' },
        { id: 'features', kind: 'features', heading: 'Features', body: 'Automations, analytics, collaboration.' },
        { id: 'integrations', kind: 'integrations', heading: 'Integrations', body: 'Connect with your tools.' },
        { id: 'pricing', kind: 'pricing', heading: 'Pricing', body: 'Simple, transparent plans.' },
        { id: 'faq', kind: 'faq', heading: 'FAQ', body: 'Security, support, onboarding' },
        { id: 'contact', kind: 'contact', heading: 'Contact', body: 'Talk to sales' },
      ],
      restaurant: [
        { id: 'hero', kind: 'hero', heading: bn, body: 'Seasonal menu. Cozy ambience. Reserve your table.' },
        { id: 'menu', kind: 'menu', heading: 'Menu Highlights', body: 'Chef specials, dietary options.' },
        { id: 'gallery', kind: 'gallery', heading: 'Gallery', body: 'A glimpse of our place and dishes.' },
        { id: 'faq', kind: 'faq', heading: 'FAQ', body: 'Reservations, parking, groups' },
        { id: 'contact', kind: 'contact', heading: 'Find Us', body: 'Location, hours, phone' },
      ],
    };

    const secs: V3Section[] = shaped[String(vertical)] || [
      { id: 'hero', kind: 'hero', heading: bn, body: `Welcome to ${bn}. ${desc}` },
      { id: 'about', kind: 'about', heading: 'About', body: desc },
      { id: 'services', kind: 'services', heading: 'Services', body: `Professional ${industry || 'services'}` },
      { id: 'contact', kind: 'contact', heading: 'Contact', body: `Get in touch at ${generatedWebsite?.contactEmail || 'info@example.com'}` },
    ];
    return { metadata: meta, sections: secs };
  };

  // Simple HTML renderer for client-side template previews (no backend dependency)
  const renderTemplatePreviewHTML = (
    key: 'modern' | 'minimal' | 'elegant' | 'bold',
    pal: 'neutral' | 'cool' | 'warm' | 'vibrant',
    meta: V3Metadata,
    secs: V3Section[]
  ): string => {
    const title = (meta as any)?.title || 'Your Business';
    const desc = (meta as any)?.description || '';
    const colors: Record<string, { bg: string; text: string; accent: string }> = {
      neutral: { bg: '#ffffff', text: '#111827', accent: '#374151' },
      cool: { bg: '#f0f6ff', text: '#0f172a', accent: '#2563eb' },
      warm: { bg: '#fff7ed', text: '#1f2937', accent: '#f97316' },
      vibrant: { bg: '#f5f3ff', text: '#111827', accent: '#7c3aed' },
    };
    const base = colors[pal] || colors.neutral;
    const c = { ...base, accent: (brandAccent && /^#?[0-9A-Fa-f]{3,6}$/.test(brandAccent) ? (brandAccent.startsWith('#') ? brandAccent : `#${brandAccent}`) : base.accent) };
    const section = (k: string) => secs.find(s => (s.kind || '').toLowerCase() === k) || secs.find(s => s.id === k);
    const hero = section('hero');
    const about = section('about');
    const services = section('services');
    const contact = section('contact');
    const fontFamily = brandFont === 'serif' ? 'ui-serif, Georgia, "Times New Roman", serif' : brandFont === 'rounded' ? 'ui-rounded, ui-sans-serif, system-ui' : 'ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, "Apple Color Emoji", "Segoe UI Emoji"';

    const modern = `<!doctype html><html lang="en"><head><meta charset="utf-8"/><meta name="viewport" content="width=device-width, initial-scale=1"/><title>${title}</title><meta name="description" content="${(desc || '').replace(/"/g,'&quot;')}"><script src="https://cdn.tailwindcss.com"></script><script>tailwind.config={theme:{extend:{colors:{accent:'${c.accent}'}}}}</script></head>
    <body class="bg-white text-slate-800" style="font-family:${fontFamily}">
      <header class="sticky top-0 z-40 bg-white/80 backdrop-blur border-b">
        <div class="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div class="flex items-center gap-3">
            ${brandLogoPlacement === 'nav' ? '<div class="h-8 w-8 rounded bg-accent"></div>' : ''}
            <div class="font-semibold text-lg">${title}</div>
          </div>
          {/* Sector-specific fields (additional) */}
          {vertical === 'consultancy' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Discovery Call URL (optional)</label>
                <input className="w-full px-3 py-2 border rounded-lg" placeholder="https://cal.example.com/meet" value={consultancyDiscoveryUrl} onChange={e=>setConsultancyDiscoveryUrl(e.target.value)} disabled={isGenerating} />
              </div>
            </div>
          )}
          {vertical === 'saas' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Integrations (comma separated)</label>
                <input className="w-full px-3 py-2 border rounded-lg" placeholder="Slack, HubSpot, Jira" value={saasIntegrations} onChange={e=>setSaasIntegrations(e.target.value)} disabled={isGenerating} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Trial URL (optional)</label>
                <input className="w-full px-3 py-2 border rounded-lg" placeholder="https://app.example.com/signup" value={saasTrialUrl} onChange={e=>setSaasTrialUrl(e.target.value)} disabled={isGenerating} />
              </div>
              <div className="flex items-center gap-2">
                <input id="saasSSO" type="checkbox" className="rounded border-gray-300" checked={saasSSO} onChange={e=>setSaasSSO(e.target.checked)} disabled={isGenerating} />
                <label htmlFor="saasSSO" className="text-sm text-gray-700">SSO</label>
              </div>
              <div className="flex items-center gap-2">
                <input id="saasSOC2" type="checkbox" className="rounded border-gray-300" checked={saasSOC2} onChange={e=>setSaasSOC2(e.target.checked)} disabled={isGenerating} />
                <label htmlFor="saasSOC2" className="text-sm text-gray-700">SOC2</label>
              </div>
            </div>
          )}
          {vertical === 'restaurant' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reservations URL (optional)</label>
                <input className="w-full px-3 py-2 border rounded-lg" placeholder="https://res.example.com/book" value={restaurantReservationsUrl} onChange={e=>setRestaurantReservationsUrl(e.target.value)} disabled={isGenerating} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Dietary Tags (comma separated)</label>
                <input className="w-full px-3 py-2 border rounded-lg" placeholder="Vegan, Vegetarian, Gluten-free" value={restaurantDietaryTags} onChange={e=>setRestaurantDietaryTags(e.target.value)} disabled={isGenerating} />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Hours (optional)</label>
                <input className="w-full px-3 py-2 border rounded-lg" placeholder="Mon–Fri 12–22, Sat 12–23" value={restaurantHours} onChange={e=>setRestaurantHours(e.target.value)} disabled={isGenerating} />
              </div>
            </div>
          )}
          {/* Sector-specific fields */}
          {vertical === 'dental' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Booking URL (optional)</label>
                <input className="w-full px-3 py-2 border rounded-lg" placeholder="https://booking.example.com" value={dentalBookingUrl} onChange={e=>setDentalBookingUrl(e.target.value)} disabled={isGenerating} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Insurance Accepted (optional)</label>
                <input className="w-full px-3 py-2 border rounded-lg" placeholder="e.g., Bupa, Denplan" value={dentalInsuranceText} onChange={e=>setDentalInsuranceText(e.target.value)} disabled={isGenerating} />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Clinic Hours (optional)</label>
                <input className="w-full px-3 py-2 border rounded-lg" placeholder="Mon–Fri 9am–6pm, Sat 10am–4pm" value={dentalHours} onChange={e=>setDentalHours(e.target.value)} disabled={isGenerating} />
              </div>
            </div>
          )}
          {vertical === 'it_support' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">SLA Target P1 (optional)</label>
                <input className="w-full px-3 py-2 border rounded-lg" placeholder="15 minutes" value={itSlaP1} onChange={e=>setItSlaP1(e.target.value)} disabled={isGenerating} />
              </div>
            </div>
          )}
          <nav class="hidden md:flex gap-6 text-sm text-slate-600">
            <a href="#services" class="hover:text-slate-900">Services</a>
            <a href="#pricing" class="hover:text-slate-900">Pricing</a>
            <a href="#case-studies" class="hover:text-slate-900">Case Studies</a>
            <a href="#faq" class="hover:text-slate-900">FAQ</a>
          </nav>
          <a href="#contact" class="inline-block md:ml-6 bg-accent text-white px-4 py-2 rounded-lg text-sm font-medium shadow hover:opacity-90">Contact</a>
        </div>
      </header>
      <main>
        <!-- Hero -->
        <section class="bg-gradient-to-b from-slate-50 to-white">
          <div class="max-w-7xl mx-auto px-4 py-16 grid md:grid-cols-2 gap-8 items-center">
            <div>
              ${brandLogoPlacement === 'hero' ? '<div class="h-10 w-10 rounded-full bg-accent mb-4"></div>' : ''}
              <h1 class="text-4xl md:text-5xl font-extrabold tracking-tight text-slate-900">${hero?.heading || title}</h1>
              <p class="mt-4 text-lg text-slate-600">${hero?.body || desc}</p>
              <div class="mt-6 flex flex-wrap gap-3">
                <a href="#contact" class="bg-accent text-white px-6 py-3 rounded-xl font-medium shadow hover:opacity-90">Get Support</a>
                <a href="#services" class="px-6 py-3 rounded-xl font-medium border">Explore Services</a>
              </div>
            </div>
            <div class="relative">
              ${heroImageUrl && heroImageUrl.trim().length > 0
                ? `<img src="${heroImageUrl}" alt="Hero image" class="w-full h-auto rounded-2xl aspect-[4/3] object-cover"/>`
                : '<div class="aspect-[4/3] rounded-2xl bg-slate-200"></div>'}
              <div class="absolute -bottom-4 -left-4 h-20 w-20 rounded-full bg-accent/20 blur-xl"></div>
            </div>
          </div>
        </section>
        <!-- Logos strip -->
        ${showLogos ? `<section class="py-6 border-y bg-white/60">
          <div class="max-w-7xl mx-auto px-4 grid grid-cols-2 md:grid-cols-5 gap-6 items-center opacity-70">
            <div class="h-6 bg-slate-300 rounded"></div>
            <div class="h-6 bg-slate-300 rounded"></div>
            <div class="h-6 bg-slate-300 rounded"></div>
            <div class="h-6 bg-slate-300 rounded"></div>
            <div class="h-6 bg-slate-300 rounded hidden md:block"></div>
          </div>
        </section>` : ''}
        <!-- Features -->
        <section id="features" class="py-14">
          <div class="max-w-7xl mx-auto px-4">
            <h2 class="text-2xl font-bold">Why choose us</h2>
            <div class="mt-6 grid gap-6 md:grid-cols-3">
              ${['24/7 Helpdesk','Proactive Monitoring','Security & Compliance'].map((t,i)=>`<div class=\"rounded-2xl border p-6 shadow-sm hover:shadow transition\"><div class=\"h-10 w-10 rounded bg-accent/20 mb-3\"></div><div class=\"font-semibold\">${t}</div><p class=\"text-sm text-slate-600 mt-1\">Enterprise-grade support with clear SLAs and rapid response.</p></div>`).join('')}
            </div>
          </div>
        </section>
        <!-- Services -->
        <section id="services" class="py-14 bg-slate-50">
          <div class="max-w-7xl mx-auto px-4">
            <h2 class="text-2xl font-bold">${services?.heading || 'Our Services'}</h2>
            <div class="mt-6 grid gap-6 md:grid-cols-3">
              <div class="rounded-2xl border bg-white p-6">
                <div class="font-semibold">Endpoint Security</div>
                <p class="text-sm text-slate-600 mt-1">${services?.body || 'Protect devices with EDR, patching and hardening.'}</p>
              </div>
              <div class="rounded-2xl border bg-white p-6">
                <div class="font-semibold">Microsoft 365</div>
                <p class="text-sm text-slate-600 mt-1">Tenant setup, administration, governance and training.</p>
              </div>
              <div class="rounded-2xl border bg-white p-6">
                <div class="font-semibold">Backup & DR</div>
                <p class="text-sm text-slate-600 mt-1">Reliable backups and disaster recovery planning.</p>
              </div>
            </div>
          </div>
        </section>
        <!-- Case studies -->
        <section id="case-studies" class="py-14">
          <div class="max-w-7xl mx-auto px-4">
            <h2 class="text-2xl font-bold">Results we deliver</h2>
            <div class="mt-6 grid gap-6 md:grid-cols-3">
              <article class="rounded-2xl border p-6">
                <div class="text-3xl font-extrabold text-slate-900">45%↓</div>
                <p class="text-sm text-slate-600">Incidents reduced in 90 days</p>
              </article>
              <article class="rounded-2xl border p-6">
                <div class="text-3xl font-extrabold text-slate-900">99.9%</div>
                <p class="text-sm text-slate-600">Uptime with proactive monitoring</p>
              </article>
              <article class="rounded-2xl border p-6">
                <div class="text-3xl font-extrabold text-slate-900"><span class="text-accent">24/7</span></div>
                <p class="text-sm text-slate-600">Round-the-clock support</p>
              </article>
            </div>
          </div>
        </section>
        <!-- Pricing -->
        <section id="pricing" class="py-14 bg-slate-50">
          <div class="max-w-7xl mx-auto px-4">
            <h2 class="text-2xl font-bold">Simple, transparent pricing</h2>
            <div class="mt-6 grid gap-6 md:grid-cols-3">
              ${[['Starter','£499/mo','Email support'],['Growth','£999/mo','Priority helpdesk'],['Enterprise','Custom','Dedicated TAM']].map(([plan,price,perk],i)=>`
                <div class=\"rounded-2xl border bg-white p-6 ${i===1?'ring-2 ring-accent':''}\">
                  <div class=\"text-sm uppercase tracking-wide text-slate-500\">${plan}</div>
                  <div class=\"mt-2 text-3xl font-extrabold\">${price}</div>
                  <ul class=\"mt-3 text-sm text-slate-600 space-y-1\">
                    <li>• ${perk}</li>
                    <li>• Monitoring & patching</li>
                    <li>• Security baseline</li>
                  </ul>
                  <a href=\"#contact\" class=\"inline-block mt-4 bg-accent text-white px-4 py-2 rounded-lg text-sm\">Choose ${plan}</a>
                </div>`).join('')}
            </div>
          </div>
        </section>
        <!-- Testimonials -->
        <section id="testimonials" class="py-14">
          <div class="max-w-7xl mx-auto px-4">
            <h2 class="text-2xl font-bold">What clients say</h2>
            <div class="mt-6 grid gap-6 md:grid-cols-2">
              <blockquote class="rounded-2xl border p-6 bg-white">
                <p class="text-slate-700">“Outstanding response times and proactive advice—our downtime is near zero.”</p>
                <footer class="mt-3 text-sm text-slate-500">— Sarah L., Operations Director</footer>
              </blockquote>
              <blockquote class="rounded-2xl border p-6 bg-white">
                <p class="text-slate-700">“They migrated us to M365 seamlessly and improved our security posture.”</p>
                <footer class="mt-3 text-sm text-slate-500">— James R., CTO</footer>
              </blockquote>
            </div>
          </div>
        </section>
        <!-- FAQ -->
        <section id="faq" class="py-14 bg-slate-50">
          <div class="max-w-4xl mx-auto px-4">
            <h2 class="text-2xl font-bold">Frequently asked questions</h2>
            <div class="mt-6 space-y-3">
              ${[['How fast is your response time?','P1 within 15 minutes, others within 1 business hour.'],['Can you support remote teams?','Yes, we support hybrid and fully remote environments.'],['Do you offer security audits?','Yes, we offer baselines, hardening and recommendations.']].map(([q,a])=>`<details class=\"rounded-xl border bg-white p-4\"><summary class=\"font-medium cursor-pointer\">${q}</summary><p class=\"mt-2 text-sm text-slate-600\">${a}</p></details>`).join('')}
            </div>
          </div>
        </section>
        <!-- Strong CTA -->
        <section class="py-16">
          <div class="max-w-7xl mx-auto px-4 text-center">
            <div class="rounded-2xl bg-gradient-to-r from-accent to-accent/80 text-white p-10">
              <h2 class="text-3xl font-extrabold">Ready to level up your IT?</h2>
              <p class="mt-2 opacity-90">Let’s design a support plan that fits your business.</p>
              <a href="#contact" class="inline-block mt-4 bg-white text-slate-900 px-6 py-3 rounded-xl font-semibold">Talk to us</a>
            </div>
          </div>
        </section>
        <!-- Contact form (non-functional) -->
        <section id="contact" class="py-16 border-t">
          <div class="max-w-3xl mx-auto px-4">
            <h2 class="text-2xl font-bold">${contact?.heading || 'Contact'}</h2>
            <p class="mt-2 text-slate-600">${contact?.body || 'Email us at info@example.com or send a message below.'}</p>
            <form class="mt-6 grid gap-4 md:grid-cols-2">
              <input class="border rounded-lg p-3 md:col-span-1" placeholder="Your name" />
              <input class="border rounded-lg p-3 md:col-span-1" placeholder="Email" />
              <textarea class="border rounded-lg p-3 md:col-span-2" rows="4" placeholder="How can we help?"></textarea>
              <button class="bg-accent text-white px-5 py-3 rounded-lg md:col-span-2">Send</button>
            </form>
          </div>
        </section>
      </main>
      <footer class="py-8 border-t">
        <div class="max-w-7xl mx-auto px-4 text-sm text-slate-500">© ${new Date().getFullYear()} ${title}</div>
      </footer>
    </body></html>`;

    const minimal = `<!doctype html><html lang="en"><head><meta charset="utf-8"/><meta name="viewport" content="width=device-width, initial-scale=1"/><title>${title}</title><script src="https://cdn.tailwindcss.com"></script><script>tailwind.config={theme:{extend:{colors:{accent:'${c.accent}'}}}}</script></head>
    <body class="bg-white text-slate-800" style="font-family:${fontFamily}">
      <header class="border-b">
        <div class="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div class="flex items-center gap-3">${brandLogoPlacement === 'nav' ? '<div class="h-7 w-7 rounded bg-accent"></div>' : ''}<div class="font-semibold">${title}</div></div>
          <a href="#contact" class="text-sm px-4 py-2 border rounded-lg">Contact</a>
        </div>
      </header>
      <main class="max-w-6xl mx-auto px-4 py-10">
        ${brandLogoPlacement === 'hero' ? '<div class="mx-auto h-10 w-10 rounded-full bg-accent mb-4"></div>' : ''}
        <h1 class="text-4xl font-semibold tracking-tight">${hero?.heading || title}</h1>
        <p class="mt-2 text-slate-600">${hero?.body || desc}</p>
        ${showLogos ? `<section id="logos" class="mt-8">
          <div class="grid grid-cols-2 md:grid-cols-5 gap-6 opacity-70">
            <div class="h-6 bg-slate-200 rounded"></div><div class="h-6 bg-slate-200 rounded"></div><div class="h-6 bg-slate-200 rounded"></div><div class="h-6 bg-slate-200 rounded"></div><div class="h-6 bg-slate-200 rounded hidden md:block"></div>
          </div>
        </section>` : ''}
        <section id="features" class="mt-10">
          <h2 class="text-xl font-semibold">Why choose us</h2>
          <div class="mt-4 grid gap-4 md:grid-cols-3">
            <div class="border rounded-2xl p-6"><div class="font-medium">24/7 Support</div><p class="text-sm text-slate-600 mt-1">We’re here when you need us.</p></div>
            <div class="border rounded-2xl p-6"><div class="font-medium">Proactive</div><p class="text-sm text-slate-600 mt-1">Issues prevented, not just fixed.</p></div>
            <div class="border rounded-2xl p-6"><div class="font-medium">Secure</div><p class="text-sm text-slate-600 mt-1">Best-practice security by default.</p></div>
          </div>
        </section>
        <section id="services" class="mt-10">
          <h2 class="text-xl font-semibold">${services?.heading || 'Services'}</h2>
          <div class="mt-4 grid gap-4 md:grid-cols-3">
            <div class="border rounded-2xl p-6"><div class="font-medium">Endpoint Security</div><p class="text-sm text-slate-600 mt-1">${services?.body || 'Protect devices with EDR and patching.'}</p></div>
            <div class="border rounded-2xl p-6"><div class="font-medium">Microsoft 365</div><p class="text-sm text-slate-600 mt-1">Admin, governance and training.</p></div>
            <div class="border rounded-2xl p-6"><div class="font-medium">Backup & DR</div><p class="text-sm text-slate-600 mt-1">Resilient backups and recovery.</p></div>
          </div>
        </section>
        <section id="pricing" class="mt-10">
          <h2 class="text-xl font-semibold">Pricing</h2>
          <div class="mt-4 grid gap-4 md:grid-cols-3">
            <div class="border rounded-2xl p-6"><div class="text-sm text-slate-500">Starter</div><div class="text-2xl font-bold mt-1">£499/mo</div><a href="#contact" class="inline-block mt-3 text-sm px-4 py-2 border rounded-lg">Choose</a></div>
            <div class="border rounded-2xl p-6 ring-1 ring-accent"><div class="text-sm text-slate-500">Growth</div><div class="text-2xl font-bold mt-1">£999/mo</div><a href="#contact" class="inline-block mt-3 text-sm px-4 py-2 border rounded-lg bg-accent text-white border-accent">Choose</a></div>
            <div class="border rounded-2xl p-6"><div class="text-sm text-slate-500">Enterprise</div><div class="text-2xl font-bold mt-1">Custom</div><a href="#contact" class="inline-block mt-3 text-sm px-4 py-2 border rounded-lg">Talk to us</a></div>
          </div>
        </section>
        <section id="faq" class="mt-10">
          <h2 class="text-xl font-semibold">FAQ</h2>
          <div class="mt-4 space-y-2">
            <details class="border rounded-xl p-4"><summary class="font-medium cursor-pointer">Response times?</summary><p class="mt-2 text-sm text-slate-600">P1 within 15 minutes; standard within 1 hour.</p></details>
            <details class="border rounded-xl p-4"><summary class="font-medium cursor-pointer">Remote support?</summary><p class="mt-2 text-sm text-slate-600">Fully supported for hybrid and remote teams.</p></details>
          </div>
        </section>
        <section id="contact" class="mt-10 border rounded-2xl p-6">
          <h2 class="text-xl font-semibold">${contact?.heading || 'Contact'}</h2>
          <p class="mt-2 text-slate-600">${contact?.body || 'Email us at info@example.com or send a message below.'}</p>
          <form class="mt-4 grid gap-3 md:grid-cols-2"><input class="border rounded-lg p-3" placeholder="Your name"/><input class="border rounded-lg p-3" placeholder="Email"/><textarea class="border rounded-lg p-3 md:col-span-2" rows="4" placeholder="How can we help?"></textarea><button class="px-4 py-2 border rounded-lg md:col-span-2">Send</button></form>
        </section>
        <footer class="mt-12 text-sm text-slate-500">© ${new Date().getFullYear()} ${title}</footer>
      </main>
    </body></html>`;

    const elegant = `<!doctype html><html lang="en"><head><meta charset="utf-8"/><meta name="viewport" content="width=device-width, initial-scale=1"/><title>${title}</title><script src="https://cdn.tailwindcss.com"></script><script>tailwind.config={theme:{extend:{colors:{accent:'${c.accent}'}}}}</script></head>
    <body class="bg-white text-slate-800" style="font-family:${fontFamily}">
      <main class="max-w-5xl mx-auto px-4 py-14 text-center">
        ${brandLogoPlacement === 'hero' ? '<div class="mx-auto h-10 w-10 rounded-full bg-accent mb-4"></div>' : ''}
        <h1 class="text-5xl md:text-6xl font-serif tracking-tight">${hero?.heading || title}</h1>
        <p class="mt-3 text-lg text-slate-600">${hero?.body || desc}</p>
        <a href="#contact" class="inline-block mt-6 bg-accent text-white px-6 py-3 rounded-full">Work With Us</a>
        ${showLogos ? `<section id="logos" class="mt-10">
          <div class="grid grid-cols-2 md:grid-cols-5 gap-6 items-center opacity-70">
            <div class="h-6 bg-slate-200 rounded"></div><div class="h-6 bg-slate-200 rounded"></div><div class="h-6 bg-slate-200 rounded"></div><div class="h-6 bg-slate-200 rounded"></div><div class="h-6 bg-slate-200 rounded hidden md:block"></div>
          </div>
        </section>` : ''}
        <div class="my-10 h-px bg-slate-200"></div>
        <section id="features" class="text-left">
          <h2 class="text-2xl font-semibold">Why choose us</h2>
          <div class="mt-6 grid gap-6 md:grid-cols-3 text-left">
            <div class="rounded-2xl border p-6"><div class="text-accent font-semibold">Craft</div><p class="mt-2 text-slate-600">Attention to detail from planning to delivery.</p></div>
            <div class="rounded-2xl border p-6"><div class="text-accent font-semibold">Care</div><p class="mt-2 text-slate-600">We partner long-term and act as an extension of your team.</p></div>
            <div class="rounded-2xl border p-6"><div class="text-accent font-semibold">Credibility</div><p class="mt-2 text-slate-600">Proven outcomes and delighted clients.</p></div>
          </div>
        </section>
        <div class="my-10 h-px bg-slate-200"></div>
        <section id="services" class="text-left">
          <h2 class="text-2xl font-semibold">${services?.heading || 'Services'}</h2>
          <div class="mt-6 grid gap-6 md:grid-cols-3">
            <div class="rounded-2xl border p-6"><div class="font-semibold">Endpoint Security</div><p class="mt-2 text-slate-600">${services?.body || 'Protect devices with EDR and patching.'}</p></div>
            <div class="rounded-2xl border p-6"><div class="font-semibold">Microsoft 365</div><p class="mt-2 text-slate-600">Admin, governance and training.</p></div>
            <div class="rounded-2xl border p-6"><div class="font-semibold">Backup & DR</div><p class="mt-2 text-slate-600">Resilient backups and recovery.</p></div>
          </div>
        </section>
        <div class="my-10 h-px bg-slate-200"></div>
        <section id="pricing" class="text-left">
          <h2 class="text-2xl font-semibold">Pricing</h2>
          <div class="mt-6 grid gap-6 md:grid-cols-3">
            <div class="rounded-2xl border p-6"><div class="text-sm text-slate-500">Starter</div><div class="text-3xl font-extrabold mt-1">£499/mo</div><a href="#contact" class="inline-block mt-3 px-4 py-2 rounded-full border">Choose</a></div>
            <div class="rounded-2xl border p-6 ring-1 ring-accent"><div class="text-sm text-slate-500">Growth</div><div class="text-3xl font-extrabold mt-1">£999/mo</div><a href="#contact" class="inline-block mt-3 px-4 py-2 rounded-full bg-accent text-white border border-accent">Choose</a></div>
            <div class="rounded-2xl border p-6"><div class="text-sm text-slate-500">Enterprise</div><div class="text-3xl font-extrabold mt-1">Custom</div><a href="#contact" class="inline-block mt-3 px-4 py-2 rounded-full border">Talk to us</a></div>
          </div>
        </section>
        <div class="my-10 h-px bg-slate-200"></div>
        <section id="testimonials" class="text-left">
          <h2 class="text-2xl font-semibold">Testimonials</h2>
          <div class="mt-6 grid gap-6 md:grid-cols-2">
            <blockquote class="rounded-2xl border p-6 bg-white">
              <p class="text-slate-700 italic">“Impeccable service and thoughtful guidance at every step.”</p>
              <footer class="mt-3 text-sm text-slate-500">— Emily P., COO</footer>
            </blockquote>
            <blockquote class="rounded-2xl border p-6 bg-white">
              <p class="text-slate-700 italic">“A trusted partner who delivers measurable results.”</p>
              <footer class="mt-3 text-sm text-slate-500">— Daniel M., Founder</footer>
            </blockquote>
          </div>
        </section>
        <div class="my-10 h-px bg-slate-200"></div>
        <section id="faq" class="text-left">
          <h2 class="text-2xl font-semibold">Frequently asked questions</h2>
          <div class="mt-6 space-y-3 text-left">
            <details class="rounded-xl border bg-white p-4"><summary class="font-medium cursor-pointer">How do we start?</summary><p class="mt-2 text-sm text-slate-600">We begin with a short discovery call and a tailored proposal.</p></details>
            <details class="rounded-xl border bg-white p-4"><summary class="font-medium cursor-pointer">What’s included?</summary><p class="mt-2 text-sm text-slate-600">Essential services, support SLAs, and quarterly reviews.</p></details>
          </div>
        </section>
        <div class="my-10 h-px bg-slate-200"></div>
        <section id="contact" class="text-left">
          <h2 class="text-2xl font-semibold">${contact?.heading || 'Contact'}</h2>
          <p class="mt-3 text-slate-600">${contact?.body || 'Email us at info@example.com or send a message below.'}</p>
          <form class="mt-6 grid gap-4 md:grid-cols-2 text-left">
            <input class="border rounded-lg p-3 md:col-span-1" placeholder="Your name" />
            <input class="border rounded-lg p-3 md:col-span-1" placeholder="Email" />
            <textarea class="border rounded-lg p-3 md:col-span-2" rows="4" placeholder="How can we help?"></textarea>
            <button class="px-6 py-3 rounded-full bg-accent text-white md:col-span-2">Send</button>
          </form>
        </section>
        <footer class="mt-12 text-sm text-slate-500">  ${new Date().getFullYear()} ${title}</footer>
      </main>
    </body></html>`;

    const bold = `<!doctype html><html lang="en"><head><meta charset="utf-8"/><meta name="viewport" content="width=device-width, initial-scale=1"/><title>${title}</title><script src="https://cdn.tailwindcss.com"></script><script>tailwind.config={theme:{extend:{colors:{accent:'${c.accent}'}}}}</script></head>
    <body class="bg-slate-950 text-slate-100" style="font-family:${fontFamily}">
      <div class="relative overflow-hidden">
        <div class="absolute inset-0 bg-gradient-to-b from-slate-900/60 to-slate-950 pointer-events-none"></div>
        <header class="relative z-10">
          <div class="max-w-7xl mx-auto px-4 py-5 flex items-center justify-between">
            <div class="font-semibold text-lg">${title}</div>
            <a href="#contact" class="bg-accent text-slate-900 px-4 py-2 rounded-lg text-sm font-semibold">Contact</a>
          </div>
        </header>
        <section class="relative z-10 text-center py-20">
          ${brandLogoPlacement === 'hero' ? '<div class="mx-auto h-12 w-12 rounded-full bg-accent mb-4"></div>' : ''}
          <h1 class="text-5xl md:text-6xl font-extrabold tracking-tight">${hero?.heading || title}</h1>
          <p class="mt-4 text-lg text-slate-300">${hero?.body || desc}</p>
          ${heroImageUrl && heroImageUrl.trim().length > 0 ? `<img src="${heroImageUrl}" class="mt-6 w-full h-96 object-cover rounded-2xl" alt="Hero image">` : ''}
          <a href="#contact" class="inline-block mt-6 bg-accent text-slate-900 px-6 py-3 rounded-xl font-semibold">Get Started</a>
        </section>
      </div>
      <main class="max-w-7xl mx-auto px-4 py-14">
        <section id="about" class="py-8">
          <h2 class="text-2xl font-bold">${about?.heading || 'About'}</h2>
          <p class="mt-3 text-slate-300">${about?.body || desc}</p>
        </section>
        <section id="services" class="py-8">
          <h2 class="text-2xl font-bold">${services?.heading || 'Services'}</h2>
          <div class="mt-6 grid gap-6 md:grid-cols-2">
            <div class="rounded-2xl border border-slate-800 bg-slate-900/60 p-6">
              <div class="font-semibold">Offering A</div>
              <p class="text-sm text-slate-300 mt-1">${services?.body || 'Service A description'}</p>
            </div>
            <div class="rounded-2xl border border-slate-800 bg-slate-900/60 p-6">
              <div class="font-semibold">Offering B</div>
              <p class="text-sm text-slate-300 mt-1">Service B description</p>
            </div>
          </div>
        </section>
        <section id=\"pricing\" class=\"py-8\"><h2 class=\"text-2xl font-bold\">Pricing</h2><div class=\"mt-6 grid gap-6 md:grid-cols-3\"><div class=\"rounded-2xl border border-slate-800 bg-slate-900/60 p-6\"><div class=\"text-sm text-slate-400\">Starter</div><div class=\"text-3xl font-extrabold mt-1\">£499/mo</div><a href=\"#contact\" class=\"inline-block mt-3 px-4 py-2 rounded-lg bg-accent text-slate-900 text-sm\">Choose</a></div><div class=\"rounded-2xl border border-slate-800 bg-slate-900/60 p-6 ring-2 ring-accent\"><div class=\"text-sm text-slate-400\">Growth</div><div class=\"text-3xl font-extrabold mt-1\">£999/mo</div><a href=\"#contact\" class=\"inline-block mt-3 px-4 py-2 rounded-lg bg-accent text-slate-900 text-sm\">Choose</a></div><div class=\"rounded-2xl border border-slate-800 bg-slate-900/60 p-6\"><div class=\"text-sm text-slate-400\">Enterprise</div><div class=\"text-3xl font-extrabold mt-1\">Custom</div><a href=\"#contact\" class=\"inline-block mt-3 px-4 py-2 rounded-lg bg-accent text-slate-900 text-sm\">Talk to us</a></div></div></section>
        <section id=\"faq\" class=\"py-8\"><h2 class=\"text-2xl font-bold\">FAQ</h2><div class=\"mt-4 space-y-2\"><details class=\"rounded-xl border border-slate-800 p-4 bg-slate-900/60\"><summary class=\"font-medium cursor-pointer\">Response times?</summary><p class=\"mt-2 text-sm text-slate-300\">P1 within 15 minutes.</p></details><details class=\"rounded-xl border border-slate-800 p-4 bg-slate-900/60\"><summary class=\"font-medium cursor-pointer\">Remote teams?</summary><p class=\"mt-2 text-sm text-slate-300\">Fully supported.</p></details></div></section>
        <section id="contact" class="py-8">
          <h2 class="text-2xl font-bold">${contact?.heading || 'Contact'}</h2>
          <p class="mt-3 text-slate-300">${contact?.body || 'Email us at info@example.com'}</p>
        </section>
      </main>
      <footer class="py-8 border-t border-slate-800 text-sm text-slate-400 text-center">© ${new Date().getFullYear()} ${title}</footer>
    </body></html>`;

    const map: Record<typeof key, string> = { modern, minimal, elegant, bold } as any;
    return map[key] || modern;
  };

  const openTemplatePreview = async (tplKey?: 'modern' | 'minimal' | 'elegant' | 'bold') => {
    setTemplatePreviewError('');
    setTemplatePreviewOpen(true);
    const key = tplKey || templateKey;
    setPreviewTemplateKey(key);
    setTemplatePreviewLoading(true);
    try {
      const { metadata, sections } = buildPreviewContent();
      const html = renderTemplatePreviewHTML(key, palette, metadata, sections);
      setTemplatePreviewHtml(html);
    } catch (e: any) {
      setTemplatePreviewError('Failed to build template preview');
    } finally {
      setTemplatePreviewLoading(false);
    }
  };

  const switchTemplateInPreview = async (key: 'modern' | 'minimal' | 'elegant' | 'bold') => {
    await openTemplatePreview(key);
  };

  // Apply the currently previewed template to the generated site by re-assembling
  const applyPreviewTemplateToSite = async () => {
    try {
      if (!user || !v3Metadata || v3Sections.length === 0) {
        setTemplatePreviewError('Nothing to apply. Generate a site first.');
        return;
      }
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token || '';
      const assembled: AssembleResponse = await requestJson(
        '/api/website/generate-v3/assemble',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            metadata: { ...v3Metadata, bookingUrl: dentalBookingUrl || undefined, insuranceText: dentalInsuranceText || undefined, hours: (vertical === 'dental' ? dentalHours : restaurantHours) || undefined, slaP1: itSlaP1 || undefined, discoveryUrl: consultancyDiscoveryUrl || undefined, integrations: saasIntegrations || undefined, trialUrl: saasTrialUrl || undefined, sso: saasSSO || undefined, soc2: saasSOC2 || undefined, reservationsUrl: restaurantReservationsUrl || undefined, dietaryTags: restaurantDietaryTags || undefined },
            sections: v3Sections,
            userId: user.id,
            templateKey: previewTemplateKey,
            palette: palette || undefined,
            brandAccent: brandAccent || undefined,
            brandFont: brandFont || undefined,
            brandLogoPlacement: brandLogoPlacement || undefined,
            heroImageUrl: heroImageUrl || undefined,
            showLogos,
            vertical: vertical || undefined,
          }),
        },
        'Failed to apply template'
      );
      if (assembled && typeof assembled.html === 'string') {
        setGeneratedWebsite((prev) => prev ? { ...prev, html: assembled.html as string } : { html: assembled.html as string } as GeneratedWebsite);
        setTemplateKey(previewTemplateKey);
        setTemplatePreviewOpen(false);
        setStatusMessage('Template applied');
        setTimeout(() => setStatusMessage(''), 2500);
      }
    } catch (e: any) {
      setTemplatePreviewError(e?.message || 'Failed to apply template');
    }
  };

  // Generate multiple variants and show a selection gallery
  const handleGenerateVariants = async (count = variantCount) => {
    if (!businessName.trim()) {
      setError('Please provide a Business Name');
      return;
    }
    if (!user) {
      setError('You must be logged in to generate variants');
      return;
    }
    setIsGeneratingVariants(true);
    setVariants([]);
    setVariantsOpen(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token || '';
      const tasks = Array.from({ length: Math.max(1, Math.min(12, count)) }).map(async (_v, idx) => {
        const payload: any = {
          businessName,
          industry,
          location: location || undefined,
          tone: tone || undefined,
          colorMood: colorMood || undefined,
          templateKey: templateKey || undefined,
          palette: palette || undefined,
          brandAccent: brandAccent || undefined,
          brandFont: brandFont || undefined,
          brandLogoPlacement: brandLogoPlacement || undefined,
          heroImageUrl: heroImageUrl || undefined,
          showLogos,
          logoUrl: logoUrl || undefined,
          targetAudience: targetAudience || undefined,
          description: (variantPrompt?.trim() || websiteDescription) || undefined,
          vertical: vertical || undefined,
          userId: user.id,
          // Optional: add a hint to backend to randomize or diversify
          variantIndex: idx,
        };
        const data: GenerateV3Response = await requestJson(
          '/api/website/generate-v3',
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify(payload),
          },
          'Failed to generate variant'
        );
        const md = data.v3?.metadata || null;
        const sec = Array.isArray(data.v3?.sections) ? (data.v3!.sections as V3Section[]) : [];
        // Build a client-side preview if html missing
        let websiteData = data.websiteData || null;
        if ((!websiteData?.html || (websiteData.html || '').trim().length === 0) && md && sec.length > 0) {
          const html = renderTemplatePreviewHTML(templateKey, palette, md, sec);
          websiteData = { ...(websiteData || {} as any), html } as GeneratedWebsite;
        }
        return { id: `${Date.now()}_${idx}`, websiteData, metadata: md, sections: sec };
      });
      const results = await Promise.allSettled(tasks);
      const ok: Array<{ id: string; websiteData: GeneratedWebsite | null; metadata: V3Metadata | null; sections: V3Section[] }> = [];
      results.forEach(r => { if (r.status === 'fulfilled') ok.push(r.value); });
      setVariants(ok);
      if (ok.length === 0) {
        setError('Failed to generate variants. Please try again.');
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to generate variants');
    } finally {
      setIsGeneratingVariants(false);
    }
  };

  const applyVariant = (v: { id: string; websiteData: GeneratedWebsite | null; metadata: V3Metadata | null; sections: V3Section[] }) => {
    setGeneratedWebsite(v.websiteData || null);
    setV3Metadata(v.metadata || null);
    setV3Sections(Array.isArray(v.sections) ? v.sections : []);
    setVariantsOpen(false);
    setPhase('editing');
  };

  // Favorites helper (top-level)
  const toggleFavoriteVariant = (id: string) => {
    setFavoriteVariantIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Poll deployment status if a deploymentId is available
  const startDeploymentPolling = async (depId: string) => {
    if (deploymentPollRef.current) {
      window.clearInterval(deploymentPollRef.current);
      deploymentPollRef.current = null;
    }
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token || '';
      deploymentPollRef.current = window.setInterval(async () => {
        try {
          const status = await requestJson(
            `/api/website/deploy/status?deploymentId=${encodeURIComponent(depId)}`,
            {
              method: 'GET',
              headers: { 'Authorization': `Bearer ${token}` }
            },
            'Failed to get deployment status'
          );
          if (Array.isArray(status.logs)) {
            setDeploymentLogs(status.logs);
          }
          if (status.status === 'deployed' && status.url) {
            setDeploymentUrl(status.url);
            setDeploymentStatus('deployed');
            setPhase('deployed');
            if (deploymentPollRef.current) {
              window.clearInterval(deploymentPollRef.current);
              deploymentPollRef.current = null;
            }
          }
          if (status.status === 'error') {
            setDeploymentStatus('error');
            setPhase('error');
            if (deploymentPollRef.current) {
              window.clearInterval(deploymentPollRef.current);
              deploymentPollRef.current = null;
            }
          }
        } catch (e: any) {
          // keep polling but surface last error briefly
        }
      }, 3000);
    } catch {
      // ignore
    }
  };

  // Upload a logo image to Supabase Storage and return a public (or signed) URL
  const uploadLogoToSupabase = async (file: File): Promise<string> => {
    if (!user) throw new Error('You must be logged in to upload a logo');
    // Basic validation
    const allowed = ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'];
    if (!allowed.includes(file.type)) {
      throw new Error('Please upload a PNG, JPG, WEBP, or SVG image');
    }
    if (file.size > 5 * 1024 * 1024) {
      throw new Error('Logo must be under 5MB');
    }

    setIsUploadingLogo(true);
    try {
      const ext = file.name.split('.').pop() || 'png';
      const path = `${user.id}/logos/${Date.now()}.${ext}`;
      const { data: uploadRes, error: uploadErr } = await supabase
        .storage
        .from('website-logos')
        .upload(path, file, { upsert: false, cacheControl: '3600', contentType: file.type });
      if (uploadErr) throw uploadErr;

      // Always use a long-lived signed URL (bucket is private with strict RLS)
      const storagePath = uploadRes.path;
      setLogoStoragePath(storagePath);
      const { data: signed, error: signedErr } = await supabase
        .storage
        .from('website-logos')
        .createSignedUrl(storagePath, 60 * 60 * 24 * 365); // 1 year
      if (signedErr) throw signedErr;
      if (!signed?.signedUrl) throw new Error('Failed to retrieve logo URL');
      return signed.signedUrl;
    } finally {
      setIsUploadingLogo(false);
    }
  };

  const onLogoFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setError('');
      const objectUrl = URL.createObjectURL(file);
      setLogoPreview(objectUrl);
      const uploadedUrl = await uploadLogoToSupabase(file);
      setLogoUrl(uploadedUrl);
      // Reflect into generated preview if present
      setGeneratedWebsite((prev: any) => prev ? { ...prev, businessLogoUrl: uploadedUrl } : prev);
    } catch (err: any) {
      setError(err.message || 'Failed to upload logo');
    }
  };

  // Map internal section kinds to user-friendly labels
  const sectionLabel = (kind?: string) => {
    const k = (kind || '').toLowerCase();
    switch (k) {
      case 'hero':
        return 'Home';
      case 'about':
        return 'About';
      case 'services':
        return 'Services';
      case 'socialproof':
      case 'testimonials':
        return 'Testimonials';
      case 'contact':
        return 'Contact';
      case 'faq':
        return 'FAQ';
      default:
        return kind || 'Section';
    }
  };

  const handleRegenerateSection = async (section: V3Section) => {
    if (!user || !v3Metadata) {
      setError('You must be logged in to regenerate sections');
      return;
    }
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token || '';

      const planItem = { id: section.id, kind: section.kind, purpose: 'Improve quality and uniqueness for this section' };

      const regen = await requestJson(
        '/api/website/generate-v3/section',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ metadata: v3Metadata, planItem, userId: user.id }),
        },
        'Failed to regenerate section'
      );

      const updatedSections = v3Sections.map(s => (s.id === section.id ? regen.section : s));
      setV3Sections(updatedSections);

      // Re-assemble HTML
      const assembled = await requestJson(
        '/api/website/generate-v3/assemble',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ metadata: { ...(v3Metadata || {}), bookingUrl: dentalBookingUrl || undefined, insuranceText: dentalInsuranceText || undefined, hours: (vertical === 'dental' ? dentalHours : restaurantHours) || undefined, slaP1: itSlaP1 || undefined, discoveryUrl: consultancyDiscoveryUrl || undefined, integrations: saasIntegrations || undefined, trialUrl: saasTrialUrl || undefined, sso: saasSSO || undefined, soc2: saasSOC2 || undefined, reservationsUrl: restaurantReservationsUrl || undefined, dietaryTags: restaurantDietaryTags || undefined }, sections: updatedSections, userId: user.id, templateKey: templateKey || undefined, palette: palette || undefined, brandAccent: brandAccent || undefined, brandFont: brandFont || undefined, brandLogoPlacement: brandLogoPlacement || undefined, heroImageUrl: heroImageUrl || undefined, showLogos, vertical: vertical || undefined }),
        },
        'Failed to assemble website'
      );

      setGeneratedWebsite((prev) => prev ? { ...prev, html: assembled.html } : prev);
      setStatusMessage('Section updated');
      setTimeout(() => setStatusMessage(''), 2000);
    } catch (err: any) {
      setError(err.message || 'Failed to regenerate section');
    }
  };

  // Debounced assemble to refresh preview when a section is edited
  const scheduleAssemble = (metadata: V3Metadata, sections: V3Section[]) => {
    if (assembleTimeoutRef.current) {
      clearTimeout(assembleTimeoutRef.current);
    }
    // cancel inflight assemble fetch
    if (assembleAbortRef.current) {
      assembleAbortRef.current.abort();
      assembleAbortRef.current = null;
    }
    assembleTimeoutRef.current = setTimeout(async () => {
      try {
        // setup abort
        assembleAbortRef.current = new AbortController();
        const { data: sessionData } = await supabase.auth.getSession();
        const token = sessionData.session?.access_token || '';
        const assembled: AssembleResponse = await requestJson(
          '/api/website/generate-v3/assemble',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ metadata: { ...metadata, bookingUrl: dentalBookingUrl || undefined, insuranceText: dentalInsuranceText || undefined, hours: (vertical === 'dental' ? dentalHours : restaurantHours) || undefined, slaP1: itSlaP1 || undefined, discoveryUrl: consultancyDiscoveryUrl || undefined, integrations: saasIntegrations || undefined, trialUrl: saasTrialUrl || undefined, sso: saasSSO || undefined, soc2: saasSOC2 || undefined, reservationsUrl: restaurantReservationsUrl || undefined, dietaryTags: restaurantDietaryTags || undefined }, sections, userId: user?.id, templateKey: templateKey || undefined, palette: palette || undefined, brandAccent: brandAccent || undefined, brandFont: brandFont || undefined, brandLogoPlacement: brandLogoPlacement || undefined, heroImageUrl: heroImageUrl || undefined, showLogos, vertical: vertical || undefined }),
            signal: assembleAbortRef.current.signal,
          },
          'Failed to assemble website'
        );
        if (assembled && typeof assembled.html === 'string') {
          setGeneratedWebsite((prev) => prev ? { ...prev, html: assembled.html as string } : prev);
        }
      } catch (e: any) {
        if (e?.name === 'AbortError') return;
        monitorError('assemble_error', e);
        setError(e.message || 'Failed to assemble website');
      }
    }, 1000);
  };

  const handleSectionChange = (sectionId: string, newText: string) => {
    setV3Sections((prev) => {
      const updated = prev.map((s) => (s.id === sectionId ? { ...s, body: newText } : s));
      if (v3Metadata) scheduleAssemble(v3Metadata, updated);
      return updated;
    });
  };

  // Track last generate attempt to debounce rapid repeats
  const lastGenAtRef = useRef<number>(0);

  const handleGenerateWebsite = async () => {
    // If already generating, ignore
    if (isGenerating) return;
    // Debounce: ignore if last attempt was within 5s
    const nowTs = Date.now();
    if (nowTs - (lastGenAtRef.current || 0) < 5000) {
      setStatusMessage('Please wait a moment before trying again.');
      setTimeout(() => setStatusMessage(''), 2500);
      return;
    }
    lastGenAtRef.current = nowTs;
    setAttemptedGenerate(true);
    if (!businessName.trim()) {
      setError('Please provide a Business Name');
      return;
    }
    if (!user) {
      setError('You must be logged in to generate a website');
      return;
    }

    setIsGenerating(true);
    setError('');
    setStatusMessage('');
    setPhase('generating');

    try {
      // Get the authentication token from Supabase
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token || '';

      // Derive industry if not provided
      const verticalToIndustry: Record<string, string> = {
        it_support: 'IT Support',
        saas: 'SaaS',
        consultancy: 'Consultancy',
        dental: 'Dental Clinic',
        restaurant: 'Restaurant',
        construction: 'Construction',
      };
      const guessFromVertical = vertical ? (verticalToIndustry[vertical] || vertical) : '';
      const guessFromDescription = (() => {
        const d = (websiteDescription || '').toLowerCase();
        if (d.includes('construction')) return 'Construction';
        if (d.includes('dental') || d.includes('dentist')) return 'Dental Clinic';
        if (d.includes('restaurant') || d.includes('cafe') || d.includes('bistro')) return 'Restaurant';
        if (d.includes('software') || d.includes('saas')) return 'SaaS';
        if (d.includes('consult')) return 'Consultancy';
        if (d.includes('it ')) return 'IT Services';
        return '';
      })();
      const industryToUse = (industry && industry.trim()) || guessFromVertical || guessFromDescription || 'General Business';

      // Call the new backend API (v3) for schema-driven single-page website
      // Build an idempotency key (same for a short window to collapse duplicates)
      const idemWindow = Math.floor(Date.now() / 3000); // 3s buckets
      const idemKey = `${user.id}:${idemWindow}:${window.location.pathname}`;

      const data = await requestJson(
        `${API_BASE}/api/website/generate-v3`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'X-Idempotency-Key': idemKey
          },
          body: JSON.stringify({
            businessName,
            industry: industryToUse,
            location: location || undefined,
            tone: tone || undefined,
            colorMood: colorMood || undefined,
            templateKey: templateKey || undefined,
            palette: palette || undefined,
            brandAccent: brandAccent || undefined,
            brandFont: brandFont || undefined,
            brandLogoPlacement: brandLogoPlacement || undefined,
            heroImageUrl: heroImageUrl || undefined,
            showLogos,
            logoUrl: logoUrl || undefined,
            targetAudience: targetAudience || undefined,
            description: websiteDescription || undefined,
            vertical: vertical || undefined,
            userId: user.id,
          }),
        },
        'Failed to generate website',
        1,
        65000
      );
      if (!data || !data.websiteData) {
        throw new Error('Invalid response from server (no websiteData)');
      }
      setGeneratedWebsite(data.websiteData);
      setV3Metadata(data.v3?.metadata || null);
      const v3 = data.v3 || {};
      const sectionsArr = Array.isArray((v3 as any).sections) ? (v3 as any).sections as V3Section[] : [];
      setV3Sections(sectionsArr);

      // If backend didn't include html, assemble it client-side from metadata + sections
      if ((!data.websiteData?.html || data.websiteData.html.trim().length === 0) && data.v3?.metadata && sectionsArr.length > 0) {
        try {
          const assembled: AssembleResponse = await requestJson(
            '/api/website/generate-v3/assemble',
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({ metadata: { ...(data.v3.metadata || {}), bookingUrl: dentalBookingUrl || undefined, insuranceText: dentalInsuranceText || undefined, hours: (vertical === 'dental' ? dentalHours : restaurantHours) || undefined, slaP1: itSlaP1 || undefined, discoveryUrl: consultancyDiscoveryUrl || undefined, integrations: saasIntegrations || undefined, trialUrl: saasTrialUrl || undefined, sso: saasSSO || undefined, soc2: saasSOC2 || undefined, reservationsUrl: restaurantReservationsUrl || undefined, dietaryTags: restaurantDietaryTags || undefined }, sections: sectionsArr, userId: user.id, templateKey: templateKey || undefined, palette: palette || undefined, brandAccent: brandAccent || undefined, brandFont: brandFont || undefined, brandLogoPlacement: brandLogoPlacement || undefined, heroImageUrl: heroImageUrl || undefined, showLogos, vertical: vertical || undefined }),
            },
            'Failed to assemble website'
          );
          if (assembled && typeof assembled.html === 'string') {
            setGeneratedWebsite((prev: any) => prev ? { ...prev, html: assembled.html as string } : { html: assembled.html as string } as GeneratedWebsite);
          } else {
            throw new Error('Assemble returned empty HTML');
          }
        } catch (assembleErr: any) {
          monitorError('assemble_on_generate_error', assembleErr);
          setError(assembleErr.message || 'Generated content was empty. Please try regenerating a section.');
        }
      }
      // Navigate directly to the editor with generated content
      navigate('/website/editor', { state: { metadata: data.v3?.metadata || null, sections: sectionsArr, html: (data.websiteData?.html || generatedWebsite?.html || ''), websiteId } });
      setPhase('editing');
    } catch (err: any) {
      monitorError('generate-v3', err);
      const msg = String(err?.message || 'Failed to generate website');
      console.error('Generate V3 failed', err);
      setError(msg);
      setStatusMessage(msg.includes('timed out') ? 'Generation timed out. Please try again.' : msg);
      setPhase('error');
      setIsGenerating(false);
      return;
    }
  };

  const handleSaveWebsite = async () => {
    if (!generatedWebsite || !user) return;

    setIsSaving(true);
    setError('');
    setStatusMessage('');
    setPhase('saving');

    try {
      // Prepare website data with business information
      const websiteData = {
        ...generatedWebsite,
        businessLogoUrl: generatedWebsite.businessLogoUrl,
        businessName: generatedWebsite.businessName,
        businessAddress: generatedWebsite.businessAddress,
        aboutUs: generatedWebsite.aboutUs,
        contactEmail: generatedWebsite.contactEmail,
        contactPhone: generatedWebsite.contactPhone,
        // Include structured data for deployment pipelines that need metadata+sections
        website_data: v3Metadata && v3Sections ? { metadata: v3Metadata, sections: v3Sections } : undefined,
        // Track storage path for private bucket assets (RLS)
        logoStoragePath: logoStoragePath || undefined,
      };

      // Get the authentication token from Supabase
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token || '';

      // Call the backend API to save the website to Supabase
      const data: SaveWebsiteResponse = await requestJson(
        '/api/website/save',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            websiteData: websiteData,
            userId: user.id,
          }),
        },
        'Failed to save website'
      );
      setWebsiteId(data.website.id);
      // Show success message
      setStatusMessage('Website saved successfully!');
      setTimeout(() => setStatusMessage(''), 3000);
      setPhase('saved');
    } catch (err: any) {
      monitorError('save_error', err);
      setError(err.message || 'Failed to save website. Please try again.');
      console.error('Error saving website:', err);
      setPhase('error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeployWebsite = async () => {
    if (!websiteId || !user) {
      setError('Please save your website before deploying');
      return;
    }

    setIsDeploying(true);
    setDeploymentStatus('deploying');
    setError('');
    setStatusMessage('');
    setPhase('deploying');

    try {
      // Get the authentication token from Supabase
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token || '';

      // Call the backend API to deploy the website
      const data: DeployResponse = await requestJson(
        '/api/website/deploy',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            websiteId: websiteId,
            userId: user.id,
          }),
        },
        'Failed to deploy website'
      );
      setDeploymentUrl(data.url || null);
      if (data.deploymentId) {
        setDeploymentId(data.deploymentId || null);
        startDeploymentPolling(data.deploymentId);
      }
      setDeploymentStatus('deployed');
      // Show success message
      setStatusMessage('Website deployed successfully!');
      setTimeout(() => setStatusMessage(''), 3000);
      setPhase('deployed');
    } catch (err: any) {
      setDeploymentStatus('error');
      monitorError('deploy_error', err);
      setError(err.message || 'Failed to deploy website. Please try again.');
      console.error('Error deploying website:', err);
      setPhase('error');
    } finally {
      setIsDeploying(false);
    }
  };

  const handleViewWebsite = () => {
    if (deploymentUrl) {
      window.open(deploymentUrl, '_blank');
    }
  };

  const openPreviewNewTab = () => {
    const html = generatedWebsite?.html || '';
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank', 'noopener');
    // Optional: revoke after a short delay
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  };

  // Domain connection via modal
  const openDomainModal = () => {
    setDomainInput('');
    setDomainError('');
    setDomainModalOpen(true);
  };

  const handleConnectDomain = async (domain: string) => {
    if (!websiteId || !user) {
      throw new Error('Please save and deploy your website before connecting a domain');
    }

    // Accept subdomains; basic sanity check and defer strict validation to backend
    const domainRegex = /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;
    if (!domainRegex.test(domain)) {
      throw new Error('Please enter a valid domain name (e.g., www.example.com)');
    }

    // Get the authentication token from Supabase (required for premium-protected routes)
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData.session?.access_token || '';

    // Call the backend API to connect the domain
    const res = await requestJson(
      '/api/website/connect-domain',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          websiteId: websiteId,
          userId: user.id,
          domain: domain,
        }),
      },
      'Failed to connect domain'
    );
    if (Array.isArray(res?.dnsRecords)) {
      setDomainDnsRecords(res.dnsRecords);
    }
    setDomainVerifyStatus(res?.status || 'pending');
  };

  const handleVerifyDomain = async () => {
    if (!websiteId || !user || !domainInput.trim()) return;
    setDomainVerifying(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token || '';
      const res = await requestJson(
        '/api/website/domain/verify',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ websiteId, userId: user.id, domain: domainInput.trim() })
        },
        'Failed to verify domain'
      );
      setDomainVerifyStatus(res?.status || null);
      if (res?.status === 'verified') {
        setStatusMessage(`Domain ${domainInput.trim()} verified!`);
        setTimeout(() => setStatusMessage(''), 5000);
        setDomainModalOpen(false);
      }
    } catch (e: any) {
      setDomainError(e.message || 'Failed to verify domain');
      setDomainVerifyStatus('error');
    } finally {
      setDomainVerifying(false);
    }
  };

  const submitDomain = async () => {
    setDomainError('');
    const domain = domainInput.trim();
    if (!domain) {
      setDomainError('Please enter your domain');
      return;
    }
    setDomainSubmitting(true);
    try {
      await handleConnectDomain(domain);
      setStatusMessage(`Domain ${domain} connected successfully! It may take a few minutes to propagate.`);
      setTimeout(() => setStatusMessage(''), 5000);
      setDomainModalOpen(false);
    } catch (e: any) {
      setDomainError(e.message || 'Failed to connect domain. Please try again.');
    } finally {
      setDomainSubmitting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Step Indicator */}
      <div className="mb-4 text-sm text-gray-600">
        {phase === 'idle' && <span>Step 1 of 3: Describe your business</span>}
        {phase === 'generating' && <span>Step 1 of 3: Generating your website…</span>}
        {(phase === 'editing' || phase === 'saving' || phase === 'saved') && <span>Step 2 of 3: Review and save</span>}
        {(phase === 'deploying' || phase === 'deployed') && <span>Step 3 of 3: Deploy and connect domain</span>}
        {phase === 'error' && <span className="text-red-700">An error occurred. Please review inputs and try again.</span>}
      </div>
      <div className="mb-8">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2 inline-flex items-center gap-2">
              AI Website Generator
              <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800 border border-yellow-200">Beta</span>
            </h1>
            <p className="text-gray-600">Create a professional website with AI in seconds. Just describe what you need and we'll generate it for you.</p>
            <p className="mt-2 text-xs text-yellow-900 bg-yellow-50 border border-yellow-200 rounded px-2 py-1 inline-block">This feature is in Beta.</p>
          </div>
          <div className="shrink-0 sticky top-0 z-20 bg-white/80 backdrop-blur supports-[backdrop-filter]:bg-white/60 border rounded-lg p-2">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => handleGenerateWebsite()}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-60"
                disabled={isGenerating}
              >
                {isGenerating ? 'Generating…' : 'Generate Website'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800 text-sm">{error}</p>
        </div>
      )}
      {statusMessage && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="text-green-800 text-sm">{statusMessage}</p>
        </div>
      )}

      {/* V3 Section Controls */}
      {v3Sections && v3Sections.length > 0 && (
        <div className="bg-white rounded-xl shadow-md p-6 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Sections</h2>
          <div className="space-y-4">
            {v3Sections.map((s) => (
              <WebsiteSectionEditor
                key={s.id}
                id={s.id}
                title={s.heading || sectionLabel(s.kind)}
                html={s.body || ''}
                status="completed"
                onChange={(newText) => handleSectionChange(s.id, newText)}
                onRegenerate={() => handleRegenerateSection(s)}
              />
            ))}
          </div>

          {(deploymentStatus === 'deploying' || deploymentStatus === 'deployed') && (
            <div className="mb-6">
              <button
                type="button"
                onClick={() => setShowDeploymentLogs(v => !v)}
                className="text-sm text-gray-700 underline"
              >
                {showDeploymentLogs ? 'Hide deployment logs' : 'View deployment logs'}
              </button>
              {showDeploymentLogs && (
                <div className="mt-2 rounded border border-gray-200 bg-gray-50 p-3 max-h-60 overflow-auto text-xs font-mono text-gray-800">
                  {deploymentLogs && deploymentLogs.length > 0 ? (
                    deploymentLogs.map((line, idx) => (
                      <div key={idx} className="whitespace-pre-wrap">{line}</div>
                    ))
                  ) : (
                    <div>No logs available yet.</div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Deployment-specific banners can be added if needed */}

      <div className="bg-white rounded-xl shadow-md p-6 mb-8">
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Basics</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div className="md:col-span-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Business Name</label>
              <input
                className={`w-full px-3 py-2 border rounded-lg ${attemptedGenerate && !businessName.trim() ? 'border-red-400 focus:ring-red-500 focus:border-red-500' : ''}`}
                value={businessName}
                onChange={e=>setBusinessName(e.target.value)}
                disabled={isGenerating}
              />
              {attemptedGenerate && !businessName.trim() && (
                <p className="mt-1 text-xs text-red-600">Business Name is required.</p>
              )}
            </div>
            {/* Business Type moved to More options */}
          </div>
          <label htmlFor="websiteDescription" className="block text-sm font-medium text-gray-700 mb-2">Describe your website</label>
          <textarea
            id="websiteDescription"
            rows={4}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Describe your website including goals, tone, and any key sections."
            value={websiteDescription}
            onChange={(e) => setWebsiteDescription(e.target.value)}
            disabled={isGenerating}
          />
          {null}
        </div>
      </div>

      {generatedWebsite && (
        <div className="bg-white rounded-xl shadow-md p-6 mb-8">
          {/* Live HTML Preview */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-medium text-gray-900">Live Preview</h3>
              <div className="flex items-center gap-2">
                <button type="button" onClick={()=>setPreviewTall(v=>!v)} className="px-3 py-1.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-xs">{previewTall ? 'Short view' : 'Tall view'}</button>
                <span className="text-xs text-gray-500">HTML size: {(generatedWebsite?.html?.length || 0).toLocaleString()} chars</span>
                <button
                  type="button"
                  onClick={openPreviewNewTab}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Open in new tab
                </button>
              </div>
            </div>
            <div className={`border rounded-lg overflow-hidden ${previewTall ? 'h-[1200px]' : 'h-[700px]'}`}>
              <iframe
                title="Website Preview"
                className="w-full h-full"
                sandbox="allow-scripts"
                srcDoc={generatedWebsite?.html || '<!doctype html><html><head><meta charset=\'utf-8\'><title>Preview unavailable</title></head><body><p style=\'font-family:ui-sans-serif,system-ui,sans-serif;padding:16px;color:#374151\'>No preview available.</p></body></html>'}
              />
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <button
              onClick={handleSaveWebsite}
              disabled={!canSave}
              className={`flex-1 font-medium py-3 px-6 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 transition duration-200 ${
                websiteId 
                  ? 'bg-green-100 text-green-800 cursor-default' 
                  : 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white hover:from-blue-600 hover:to-indigo-700 focus:ring-blue-500'
              }`}
            >
              {isSaving ? (
                <div className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Saving...
                </div>
              ) : websiteId ? (
                'Saved Successfully'
              ) : (
                'Save Website'
              )}
            </button>
            
            <button
              onClick={handleDeployWebsite}
              disabled={!canDeploy}
              className={`flex-1 font-medium py-3 px-6 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 transition duration-200 ${
                deploymentStatus === 'deployed' 
                  ? 'bg-green-100 text-green-800 cursor-default' 
                  : 'bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:from-green-600 hover:to-emerald-700 focus:ring-green-500'
              }`}
            >
              {isDeploying ? (
                <div className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Deploying...
                </div>
              ) : deploymentStatus === 'deployed' ? (
                'Deployed Successfully'
              ) : (
                'Deploy Website'
              )}
            </button>
          </div>
          
          {deploymentStatus === 'deployed' && deploymentUrl && (
            <div className="mb-6 p-4 bg-green-50 rounded-lg border border-green-200">
              <h3 className="font-medium text-green-800 mb-2">Your website is live!</h3>
              <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
              <a 
                href={deploymentUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-green-700 hover:text-green-900 font-medium underline"
              >
                {deploymentUrl}
              </a>
              <button
                onClick={handleViewWebsite}
                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
              >
                View Website
              </button>
              <button
                onClick={openDomainModal}
                className="bg-white text-green-700 border border-green-300 px-4 py-2 rounded-lg hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
              >
                Connect Custom Domain
              </button>
            </div>
          </div>
          )}
          
          {error && deploymentStatus === 'deployed' && (
            <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-blue-800 text-sm">{error}</p>
            </div>
          )}
          
          <div className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={() => setGeneratedWebsite(null)}
              className="flex-1 bg-gray-200 text-gray-800 font-medium py-3 px-6 rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition duration-200"
            >
              Clear Preview
            </button>
            <button
              onClick={handleRegenerateUsingCurrentTemplate}
              className="flex-1 bg-gray-800 text-white font-medium py-3 px-6 rounded-lg hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-700 transition duration-200"
            >
              Regenerate with Current Template
            </button>
          </div>
        </div>
      )}

      {false && generatedWebsite && (
        <div />
      )}

      {/* Domain Connect Modal */}
      {domainModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true">
          <div className="w-full max-w-md rounded-xl bg-white shadow-lg" ref={domainModalRef}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Connect Custom Domain</h3>
                <button ref={domainModalCloseRef} className="text-gray-500 hover:text-gray-700" onClick={() => setDomainModalOpen(false)} aria-label="Close domain modal">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
              <p className="text-sm text-gray-600 mb-3">Enter a domain you own (e.g., <span className="font-mono">www.example.com</span>). We’ll provide DNS instructions after connecting.</p>
              {domainError && (
                <div className="mb-3 rounded border border-red-200 bg-red-50 p-2 text-sm text-red-800">{domainError}</div>
              )}
              <input
                type="text"
                placeholder="www.example.com"
                className="w-full rounded-lg border px-3 py-2"
                value={domainInput}
                onChange={(e) => setDomainInput(e.target.value)}
                disabled={domainSubmitting}
              />
              {domainDnsRecords && domainDnsRecords.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-sm font-semibold text-gray-800 mb-2">DNS Records</h4>
                  <div className="rounded border border-gray-200">
                    <div className="divide-y">
                      {domainDnsRecords.map((r: { type: string; name: string; value: string; ttl?: number }, i: number) => (
                        <div key={i} className="grid grid-cols-3 gap-2 text-xs p-2">
                          <div className="font-mono"><span className="text-gray-500">{r.type}</span></div>
                          <div className="font-mono break-all">{r.name}</div>
                          <div className="font-mono break-all">{r.value}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="mt-2 text-xs text-gray-600">Add these records at your DNS provider. Propagation can take a few minutes.</div>
                </div>
              )}
              <div className="mt-4 flex justify-end gap-3">
                <button className="rounded-lg border px-4 py-2 text-gray-700 hover:bg-gray-50" onClick={() => setDomainModalOpen(false)} disabled={domainSubmitting}>Cancel</button>
                <button className="rounded-lg bg-green-600 px-4 py-2 text-white hover:bg-green-700 disabled:opacity-60" onClick={submitDomain} disabled={domainSubmitting}>
                  {domainSubmitting ? 'Connecting…' : 'Connect Domain'}
                </button>
              </div>
              {domainDnsRecords && (
                <div className="mt-4 flex items-center justify-between">
                  <div className="text-sm text-gray-700">Verification status: {domainVerifyStatus || 'pending'}</div>
                  <button
                    className="rounded-lg bg-blue-600 px-3 py-2 text-white text-sm hover:bg-blue-700 disabled:opacity-60"
                    onClick={handleVerifyDomain}
                    disabled={domainVerifying}
                  >
                    {domainVerifying ? 'Verifying…' : 'Verify DNS'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Template Preview Modal */}
      {templatePreviewOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" role="dialog" aria-modal="true">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-5xl max-h-[90vh] overflow-y-auto" ref={templateModalRef} onKeyDown={(e) => {
            if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
              const keys: Array<'modern'|'minimal'|'elegant'|'bold'> = ['modern','minimal','elegant','bold'];
              const idx = keys.indexOf(previewTemplateKey);
              const next = e.key === 'ArrowRight' ? (idx + 1) % keys.length : (idx - 1 + keys.length) % keys.length;
              switchTemplateInPreview(keys[next]);
            }
          }}>
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold text-gray-900">Template Preview</h2>
                <div className="flex items-center gap-3">
                  <button
                    onClick={applyPreviewTemplateToSite}
                    className="px-3 py-1.5 rounded-lg bg-blue-600 text-white text-sm hover:bg-blue-700"
                  >
                    Use this template
                  </button>
                  <button 
                    ref={templateModalCloseRef}
                    onClick={() => setTemplatePreviewOpen(false)}
                    className="text-gray-500 hover:text-gray-700"
                    aria-label="Close template preview"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="mb-4 flex items-center gap-2">
                <span className="text-sm text-gray-600">Choose a template to preview:</span>
                {(['modern','minimal','elegant','bold'] as const).map(key => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => switchTemplateInPreview(key)}
                    className={`px-3 py-1.5 rounded-lg text-sm border ${previewTemplateKey === key ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                  >
                    {key.charAt(0).toUpperCase() + key.slice(1)}
                  </button>
                ))}
              </div>

              {templatePreviewError && (
                <div className="mb-4 bg-red-50 border border-red-200 rounded p-3 text-sm text-red-800">{templatePreviewError}</div>
              )}

              <div className="border rounded-lg overflow-hidden h-[800px] bg-white">
                {templatePreviewLoading ? (
                  <div className="w-full h-full flex items-center justify-center text-gray-600">Loading preview…</div>
                ) : (
                  <iframe
                    title="Template Preview"
                    className="w-full h-full"
                    sandbox="allow-scripts"
                    srcDoc={templatePreviewHtml || '<!doctype html><html><head><meta charset=\'utf-8\'><title>Preview unavailable</title></head><body><p style=\'font-family:ui-sans-serif,system-ui,sans-serif;padding:16px;color:#374151\'>No preview available.</p></body></html>'}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Removed separate Website Preview modal to simplify UX; use the inline Live Preview section below */}

      {/* Variants Gallery Modal */}
      {false && variantsOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-6xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-gray-900">Choose a Variant</h3>
                <button className="text-gray-500 hover:text-gray-700" onClick={() => setVariantsOpen(false)} aria-label="Close variants">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
              <div className="mb-3 grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                <div className="flex items-center gap-2">
                  <label className="text-gray-700">Count</label>
                  <input type="number" min={1} max={12} className="w-20 border rounded px-2 py-1" value={variantCount} onChange={e=>setVariantCount(Math.max(1, Math.min(12, Number(e.target.value)||6)))} disabled={isGeneratingVariants} />
                </div>
                <input
                  type="text"
                  placeholder="Refine prompt (optional)"
                  className="border rounded px-3 py-2"
                  value={variantPrompt}
                  onChange={(e)=>setVariantPrompt(e.target.value)}
                  disabled={isGeneratingVariants}
                />
                <div className="flex items-center justify-end gap-3">
                  <label className="flex items-center gap-2 text-gray-700"><input type="checkbox" className="rounded" checked={showFavoritesOnly} onChange={e=>setShowFavoritesOnly(e.target.checked)} /> Favorites only</label>
                  <button onClick={()=>handleGenerateVariants(variantCount)} disabled={isGeneratingVariants} className="px-3 py-1.5 rounded bg-gray-900 text-white disabled:opacity-50">{isGeneratingVariants?'Generating…':'Regenerate'}</button>
                </div>
              </div>
              {isGeneratingVariants ? (
                <div className="text-gray-600">Generating variants…</div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {(showFavoritesOnly ? variants.filter(v=>favoriteVariantIds.has(v.id)) : variants).length === 0 && (
                    <div className="text-gray-600">No variants yet. Click Regenerate.</div>
                  )}
                  {(showFavoritesOnly ? variants.filter(v=>favoriteVariantIds.has(v.id)) : variants).map(v => (
                    <div key={v.id} className="border rounded-lg overflow-hidden">
                      <div className="h-64 bg-white">
                        <iframe title={`Variant ${v.id}`} className="w-full h-full" sandbox="allow-scripts" srcDoc={(v.websiteData?.html)||'<!doctype html><html><head><meta charset=\'utf-8\'></head><body style="font-family:ui-sans-serif,system-ui,sans-serif;padding:12px;color:#374151">Preview unavailable</body></html>'}></iframe>
                      </div>
                      <div className="p-3 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <button onClick={()=>toggleFavoriteVariant(v.id)} aria-label="Toggle favorite" className={`h-6 w-6 rounded-full flex items-center justify-center ${favoriteVariantIds.has(v.id)?'bg-yellow-400':'bg-gray-200'}`}>
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className={`h-3.5 w-3.5 ${favoriteVariantIds.has(v.id)?'text-yellow-900':'text-gray-600'}`}><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.802 2.035a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.802-2.035a1 1 0 00-1.175 0l-2.802 2.035c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/></svg>
                          </button>
                          <div className="text-sm text-gray-700 truncate">{(v.metadata as any)?.title || businessName || 'Variant'}</div>
                        </div>
                        <button onClick={()=>applyVariant(v)} className="px-2.5 py-1.5 rounded bg-blue-600 text-white text-xs hover:bg-blue-700">Use</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default AIWebsiteGenerator;
